import React, { useState } from 'react';
import { Field, useField, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { ASSISTANTS_CATEGORY, ASSISTANT_TYPES, GENIE_EVALUATE_TYPE, PROJECT_TYPES } from '../../../../constants';
import clsx from 'clsx';

import useTypeOptions from '../../../../hooks/useTypeOptions';
import ChangeAssistantModal from '../../../../scout-new/components/generation-section/components/reset-session-modal/ResetSessionModal';
import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import WithError from '../../../../components/with-error/WithError';
import DropdownWithIcons from '../../../../components/common-dropdown/DropdownWithIcons';
import DocsButton from '../../../../scout-new/components/generation-section/components/docs-button/DocsButton';
import { Input } from '../../../../components/common-inputs';
import CommonButton, { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import { ICONS } from '../../../../icons';
import Icon from '../../../../icons/Icon';
import RepliesForm from './replies-form/RepliesForm';
import { changeGenerationForm } from '../../../../redux/genie/genieReducer';

import scss from './GenerateMessagesForm.module.scss';

function GenerateMessagesFormInner({ onSubmit, onReply, onResetSession, onFileUpload, projectType }) {
  const { t } = useTranslation('scout');
  const dispatch = useDispatch();
  const { id } = useParams();

  const form = useForm();
  const [possibleNewAssistant, setPossibleNewAssistants] = useState(null);

  const assistantCategory = ASSISTANTS_CATEGORY.GENERATE_ALL;

  const { input: assistantInput } = useField(`channels.${assistantCategory}.assistant`);
  const typeOptions = useTypeOptions(
    (state) => state[projectType === PROJECT_TYPES.GENIE_GO ? 'genieGo' : 'genie'].assistants[assistantCategory],
  );

  const isResetting = useSelector(
    (state) => state[projectType === PROJECT_TYPES.GENIE_GO ? 'genieGo' : 'genie'].isResetting,
  );
  const isGenerating = useSelector(
    (state) => state[projectType === PROJECT_TYPES.GENIE_GO ? 'genieGo' : 'genie'].isGenerating,
  );
  const isFileUploading = useSelector((state) => state.scout.isFileUploading);
  const isFormLoading = useSelector(
    (state) => state[projectType === PROJECT_TYPES.GENIE_GO ? 'genieGo' : 'genie'].isFormLoading,
  );
  const threadId = useSelector((state) => state[projectType === PROJECT_TYPES.GENIE_GO ? 'genieGo' : 'genie'].threadId);
  const evaluateActions = useSelector(
    (state) => state[projectType === PROJECT_TYPES.GENIE_GO ? 'genieGo' : 'genie'].evaluateActions,
  );
  const isEvaluatingGenMessages = evaluateActions[GENIE_EVALUATE_TYPE.GENERATED].pending;

  const disableTextFields = isFormLoading || isResetting || isEvaluatingGenMessages;
  const disableDocButton =
    isFormLoading ||
    isResetting ||
    isFileUploading ||
    isEvaluatingGenMessages ||
    assistantInput.value?.type !== ASSISTANT_TYPES.FILE;

  const handleAssistantClick = (newAssistant) => {
    const assistantData = typeOptions.find(({ value }) => value === newAssistant);

    if (!assistantInput.value?.id) {
      form.change(`channels.${assistantCategory}.assistant`, assistantData);
      return;
    }

    if (assistantData.id !== assistantInput.value?.id) {
      setPossibleNewAssistants(assistantData);
    }
  };

  const handleResetSession = async () => {
    await dispatch(onResetSession());
    form.restart();
  };

  const handleCancelAssistantChanges = () => {
    setPossibleNewAssistants(null);
  };

  const handleChangeAssistant = () => {
    form.change(`channels.${assistantCategory}.assistant`, possibleNewAssistant);
    form.change('fileText', '');
    setPossibleNewAssistants(null);
  };

  const handleSaveOnBlur = (e, input) => {
    input.onBlur(e);
    dispatch(changeGenerationForm({ [input.name]: e.target.value }));
  };

  const handleSaveOnChange = (e, input) => {
    input.onChange(e);
    dispatch(changeGenerationForm({ [input.name]: e }));
  };

  const handleChangeAssistantAndReset = async () => {
    await dispatch(onResetSession(id));

    form.change(`channels.${assistantCategory}.assistant`, possibleNewAssistant);
    form.change('fileText', '');
    setPossibleNewAssistants(null);
  };

  return (
    <form className={scss.form} id='genie-generation_form' onSubmit={onSubmit}>
      <div className={scss.topBlock}>
        <Field name={`channels.${assistantCategory}.assistant`}>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={t('type')} isBold extraClassName={scss.typeField}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <DropdownWithIcons
                  options={typeOptions}
                  value={input.value?.value}
                  onSelect={handleAssistantClick}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  name={input.name}
                  placeholder={t('select')}
                  isError={meta.touched && meta.error}
                  disabled={isFormLoading}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <DocsButton
          selectedTypeOption={assistantInput.value}
          onFileUpload={onFileUpload}
          disabled={disableDocButton}
          isLoading={isFileUploading}
        />

        <Field name='url' autoComplete='genie-generate-url'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label='URL' isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  name={input.name}
                  value={input.value}
                  onChange={(e) => handleSaveOnChange(e, input)}
                  onFocus={input.onFocus}
                  onBlur={(e) => handleSaveOnBlur(e, input)}
                  disabled={disableTextFields}
                  isError={meta.touched && meta.error}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='focusOn'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={t('topicIdeas')} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={(e) => handleSaveOnBlur(e, input)}
                  disabled={disableTextFields}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='byPass'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={t('bypass')} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={(e) => handleSaveOnBlur(e, input)}
                  disabled={disableTextFields}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <CommonButton
          styleColor={BUTTON_STYLES.PRIMARY}
          maxWidth={140}
          type='submit'
          disabled={isFormLoading || isEvaluatingGenMessages}
          isLoading={isGenerating}>
          {t('generate')}
        </CommonButton>

        <CommonButton
          styleColor={BUTTON_STYLES.SECONDARY}
          onClick={handleResetSession}
          maxWidth={140}
          disabled={!threadId || isFormLoading}>
          {t('resetSession')}
          <Icon icon={ICONS.Reset} className={clsx(isResetting && scss.spinAnimation)} />
        </CommonButton>
      </div>

      <RepliesForm onReply={onReply} assistantInput={assistantInput} projectType={projectType} />

      <ChangeAssistantModal
        isOpen={!!possibleNewAssistant}
        onCancel={handleCancelAssistantChanges}
        onChange={handleChangeAssistant}
        onChangeAndReset={handleChangeAssistantAndReset}
      />
    </form>
  );
}

export default GenerateMessagesFormInner;
